import { ReactNode } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { VAMP_EXCESSIVE_PCT, VAMP_SCORING_THRESHOLD } from "./vampStanding";

const THRESHOLD = `${VAMP_EXCESSIVE_PCT.toFixed(1)}%`;
const SCORING = VAMP_SCORING_THRESHOLD.toLocaleString("en-US");
// The dialog keeps its own wording; the tab's footer legend uses the shorter mockup phrasing.
const FORMULA_TEXT = "VAMP = (TC15 + TC40) / Visa Transactions";

function Section({
  title,
  defaultExpanded,
  children,
}: {
  title: string;
  defaultExpanded?: boolean;
  children: ReactNode;
}) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      defaultExpanded={defaultExpanded}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ backgroundColor: "action.hover", "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 1.5, color: "text.secondary", fontSize: 14, lineHeight: 1.6 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

const listSx = {
  m: 0,
  pl: 2.5,
  "& > li": { mb: 0.75 },
  "& > li:last-of-type": { mb: 0 },
};

interface VampInfoDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function VampInfoDialog({ open, onClose }: VampInfoDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>How VAMP rates work</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Section title="What are VAMP rates?" defaultExpanded>
            <Typography variant="body2" sx={{ mb: 1 }}>
              VAMP (Visa Acquirer Monitoring Program) measures how much fraud and how many disputes
              your account generates, as a share of your settled Visa transactions.
            </Typography>
            <Typography variant="body2">
              Below {THRESHOLD} is healthy. At or above {THRESHOLD} Visa calls it excessive, which
              can bring fines and restrictions.
            </Typography>
          </Section>

          <Section title="How are they calculated?">
            <Typography
              variant="body2"
              sx={{ textAlign: "center", fontWeight: 600, color: "text.primary", mb: 1.5 }}
            >
              {FORMULA_TEXT}
            </Typography>
            <Box component="ul" sx={{ ...listSx, mb: 1.5 }}>
              <li>
                <strong>TC40</strong> - fraud notices from the cardholder's bank.
              </li>
              <li>
                <strong>TC15</strong> - disputes.
              </li>
              <li>
                <strong>Visa Transactions</strong> - your settled Visa transactions. Other card
                brands are excluded from both sides.
              </li>
            </Box>
            <Box component="ul" sx={listSx}>
              <li>The chart covers the month you are viewing and the one before it, so the trend reads across the boundary.</li>
              <li>Data starts June 2026. Months with no settled Visa volume show 0%.</li>
            </Box>
          </Section>

          <Section title="When does Visa score a CAID?">
            <Typography variant="body2">
              Visa only scores a CAID in a month where it records at least {SCORING} TC15/TC40
              events. Below that, the CAID is not counted for VAMP that month, even if the rate looks
              high.
            </Typography>
          </Section>

          <Section title="Setup">
            <Typography variant="body2">
              Setup is creating payment-provider accounts and enrolments so TC40/TC15 notices can be
              matched. A client or support can do that.
            </Typography>
          </Section>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
